import { isCreativesSurfaceJob, sortJobsNewestFirst, type CreativeJob } from '@/store/slices/creativeJobs';

import { SITE_REGEN_KIND } from '../art-style/art-style-api';

/**
 * Which jobs make the Creatives page read its banner family again (see `useCreatives`). Pure, so the watchers
 * and the per-brand seeding of what they have already seen apply one rule.
 */

const forTenant = (job: CreativeJob, tenantId: number): boolean => String(job.tenant_id) === String(tenantId);

/** A formats run for this brand that has reached its total (done, or `completed` caught up with `total`). */
export const isFormatsRunComplete = (job: CreativeJob, tenantId: number): boolean =>
  job.kind === 'formats' &&
  forTenant(job, tenantId) &&
  !!job.parent_id &&
  (job.total ?? 0) > 0 &&
  (job.status === 'done' || (job.completed ?? 0) >= (job.total ?? 0));

/** The jobs whose end reads the family again, newest first: the creatives surface's own runs, and the brand's site jobs
 *  (a switch, a revert or a regeneration in place changes the live banners), which the surface otherwise leaves out.
 *  The cards and a redraw never change a banner (a redraw's candidate is applied separately). */
export const familyDoneWatchJobs = (byId: Record<string, CreativeJob>): CreativeJob[] =>
  sortJobsNewestFirst(byId).filter((job) => isCreativesSurfaceJob(job) || job.kind === SITE_REGEN_KIND);

/** A run for this brand that has finished. */
export const isDoneRun = (job: CreativeJob, tenantId: number): boolean =>
  job.status === 'done' && forTenant(job, tenantId);

/** The progress key (`id:completed`) of a run for this brand that has drawn at least one slot and is still going,
 *  else undefined. */
export const progressKeyOf = (job: CreativeJob, tenantId: number): string | undefined =>
  (job.status === 'running' || job.status === 'queued') && forTenant(job, tenantId) && (job.completed ?? 0) > 0
    ? `${job.id}:${job.completed}`
    : undefined;

export interface SeenFamilyJobs {
  formatsComplete: Set<string>;
  done: Set<string>;
  progress: Set<string>;
}

/**
 * What the jobs list already shows for this brand, as the watchers' seen sets. The page's first read of the
 * family already reflects these jobs, so only a job that finishes or advances after them reads it again.
 */
export const seenFamilyJobs = (jobs: CreativeJob[], tenantId: number | undefined): SeenFamilyJobs => {
  const seen: SeenFamilyJobs = { formatsComplete: new Set(), done: new Set(), progress: new Set() };
  if (!tenantId) return seen;
  for (const job of jobs) {
    if (isFormatsRunComplete(job, tenantId)) seen.formatsComplete.add(job.id);
    if (isDoneRun(job, tenantId)) seen.done.add(job.id);
    const progressKey = progressKeyOf(job, tenantId);
    if (progressKey !== undefined) seen.progress.add(progressKey);
  }
  return seen;
};
